import { useState, useRef, useLayoutEffect, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { PanelRightOpen, PanelRightClose } from 'lucide-react';
import { Sheet, SheetContent } from '../ui/Sheet';
import { Separator } from '../ui/Separator';
import { CommentThread } from '../referral-drawer/CommentThread';
import { ReferralInfoPanel } from '../referral-drawer/ReferralInfoPanel';
import { SlDispositionModal } from '../referral-drawer/SlDispositionModal';
import { fetchReferralDetail } from '../../services/referralService';
import { cn } from '../../lib/utils';
import { EngagementDrawerHeader } from './EngagementDrawerHeader';

// Single "Overview" tab for the baseline. The array shape mirrors the
// carrier-portal drawer so additional tabs slot in without a structural
// refactor — add an entry and a render branch below.
const TABS = [{ key: 'overview', label: 'Overview' }];

// Referral drawer for the Carrier Engagement Dashboard. Composes the shared
// Sheet, CommentThread, and ReferralInfoPanel with a CE-specific header. This
// baseline is read-only: the comment composer (+) and SL Response/Reason/Action
// fields are present but their edit flows are scoped to separate stories.
//
// Reuse note: the leaf components (Sheet, CommentThread, ReferralInfoPanel) are
// shared with the carrier-portal ReferralDrawer. The shell here (Sheet wiring +
// tab underline + Overview 2-col grid + collapse) is intentionally NOT shared
// yet — CP's drawer has a different header (workflow timeline), different field
// names, and a live posting flow. Once this drawer's deferred features land
// (SL edit, flags, extra tabs) the common surface will be clear; extract a
// shared ReferralDrawerShell THEN, with the CP drawer as the other consumer.
function EngagementReferralDrawer({ open, onClose, referral, canEditSl = false, onSlPosted }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [underlineStyle, setUnderlineStyle] = useState({ left: 0, width: 0 });
  const [isReferralCollapsed, setIsReferralCollapsed] = useState(false);
  const tabsRef = useRef(new Map());

  // SL-disposition modal state. Gated to SL employees via canEditSl.
  const [slModalOpen, setSlModalOpen] = useState(false);
  const [slModalField, setSlModalField] = useState(null);
  // `replaces` for the new SL disposition is the latest non-deleted SL activity
  // in the loaded thread (threads come back newest-first).
  const [replaces, setReplaces] = useState(null);
  // Bumping this re-runs CommentThread's fetch so the thread shows the new
  // comment after a post — no full drawer reload.
  const [commentsReloadKey, setCommentsReloadKey] = useState(0);

  const handleActivitiesLoaded = useCallback((activities) => {
    const match = (activities ?? []).find((a) => a.is_sl && !a.deleted_at);
    setReplaces(match?.id ?? null);
  }, []);

  const openSlModal = useCallback((field) => {
    if (!canEditSl) return;
    setSlModalField(field);
    setSlModalOpen(true);
  }, [canEditSl]);

  const handleSlPosted = useCallback((patch) => {
    // Refresh the visible thread so the new comment appears, and patch the
    // underlying table row (which also keeps this drawer's header in sync).
    setCommentsReloadKey((k) => k + 1);
    if (onSlPosted && referral?.referral_id) onSlPosted(referral.referral_id, patch);
  }, [onSlPosted, referral?.referral_id]);

  // Detail payload (summary, reason, code definitions, corrective action) is
  // fetched once per drawer open and handed to ReferralInfoPanel — same
  // endpoint the carrier portal uses, keyed on the legacy referral id.
  const [detailData, setDetailData] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);

  useEffect(() => {
    if (!open || !referral?.referral_id) {
      setDetailData(null);
      setDetailLoading(false);
      setDetailError(null);
      return undefined;
    }

    let cancelled = false;
    setDetailLoading(true);
    setDetailError(null);
    setDetailData(null);

    fetchReferralDetail(referral.referral_id)
      .then((result) => {
        if (!cancelled) setDetailData(result);
      })
      .catch((err) => {
        if (!cancelled) setDetailError(err);
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, referral?.referral_id]);

  const updateUnderline = (key) => {
    const el = tabsRef.current.get(key);
    if (el) {
      setUnderlineStyle({ left: el.offsetLeft, width: el.offsetWidth });
    }
  };

  useLayoutEffect(() => {
    if (open) updateUnderline(activeTab);
  }, [open, activeTab]);

  const handleTabClick = (key) => {
    setActiveTab(key);
    updateUnderline(key);
  };

  return (
    <Sheet open={open} onOpenChange={() => onClose?.()}>
      <SheetContent onClose={onClose}>
        <EngagementDrawerHeader
          referral={referral}
          onEditSl={canEditSl ? openSlModal : undefined}
        />

        <div className="px-6 bg-drawer-bg relative mt-2">
          <div className="relative flex gap-6 -mb-px">
            {TABS.map((tab) => (
              <button
                key={tab.key}
                ref={(el) => {
                  if (el) tabsRef.current.set(tab.key, el);
                }}
                onClick={() => handleTabClick(tab.key)}
                className={cn(
                  'px-1 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.key
                    ? 'text-foreground'
                    : 'text-muted-foreground hover:text-foreground'
                )}
              >
                {tab.label}
              </button>
            ))}
            <motion.div
              className="absolute bottom-0 h-[2.5px] rounded-full bg-primary"
              animate={{ left: underlineStyle.left, width: underlineStyle.width }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
            />
          </div>
          <Separator className="mt-0" />
        </div>

        <div className="flex-1 flex flex-col min-h-0">
          {activeTab === 'overview' && (
            <div className="flex-1 flex flex-col gap-3 px-6 py-5 bg-background min-h-0">
              <div
                className={cn(
                  'grid min-h-0 flex-1 gap-4',
                  isReferralCollapsed ? 'grid-cols-1' : 'grid-cols-2'
                )}
              >
                {/* Comments — left column */}
                <div className="flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      {/* Opens the SL-disposition modal. SL employees only —
                          non-SL viewers see the thread read-only. */}
                      {canEditSl && (
                        <button
                          type="button"
                          data-testid="add-comment-sl"
                          aria-label="Add comment and update SL response"
                          onClick={() => openSlModal('latest_comment')}
                          className="w-7 h-7 rounded border border-border flex items-center justify-center text-foreground/80 hover:bg-accent transition-colors text-lg leading-none"
                        >
                          +
                        </button>
                      )}
                      <span className="text-sm font-semibold text-foreground">
                        Comment &amp; Update SL Response
                      </span>
                    </div>
                    <button
                      type="button"
                      data-testid="collapse-toggle"
                      onClick={() => setIsReferralCollapsed((v) => !v)}
                      className="w-7 h-7 rounded border border-border flex items-center justify-center text-foreground/80 hover:bg-accent transition-colors"
                      title={isReferralCollapsed ? 'Show referral info' : 'Hide referral info'}
                    >
                      {isReferralCollapsed ? (
                        <PanelRightOpen className="h-4 w-4" />
                      ) : (
                        <PanelRightClose className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                  <div data-testid="comment-thread" className="flex flex-col min-h-0 flex-1">
                    <CommentThread
                      refId={referral?.referral_id}
                      readOnly
                      reloadKey={commentsReloadKey}
                      onActivitiesLoaded={handleActivitiesLoaded}
                      includeInternalComments
                    />
                  </div>
                </div>

                {/* Referral Info Panel — right column */}
                {!isReferralCollapsed && (
                  <div data-testid="referral-info-panel" className="flex flex-col min-h-0 h-full">
                    <ReferralInfoPanel
                      caseId={referral?.referral_id}
                      referralType={referral?.referral_type}
                      // CE list serializer exposes referral_date as publish_dt
                      // (source="referral_date"); the CP drawer reads referral_date
                      // directly. Don't "unify" these without checking the row shape.
                      referralDate={referral?.publish_dt}
                      providerName={referral?.referral_name}
                      data={detailData}
                      loading={detailLoading}
                      error={detailError}
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </SheetContent>

      {slModalOpen && (
        <SlDispositionModal
          open={slModalOpen}
          onClose={() => setSlModalOpen(false)}
          refId={referral?.referral_id}
          currentResponse={referral?.sl_response ?? null}
          initialFocusField={slModalField}
          replaces={replaces}
          needsReplacesFetch={false}
          onSuccess={handleSlPosted}
        />
      )}
    </Sheet>
  );
}

export { EngagementReferralDrawer };
